import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type {
  EstadoCotizacion,
  EstadoFactura,
  EstadoProyecto,
  Moneda,
  ProyectoCreate,
  ProyectoUpdate,
} from "@uirtus/shared";
import { api } from "./api";

export interface Proyecto {
  id: string;
  clienteId: string;
  clave: string | null;
  nombre: string;
  descripcion: string | null;
  estado: EstadoProyecto;
  moneda: Moneda;
  montoEstimado: string | null;
  fechaInicio: string | null;
  fechaFinEstimada: string | null;
  fechaFinReal: string | null;
  creadoAt: string;
}

export interface ProyectoLista extends Proyecto {
  cliente: { id: string; rfc: string; razonSocial: string };
}

export interface CotizacionDelProyecto {
  id: string;
  folio: string;
  version: number;
  titulo: string;
  estado: EstadoCotizacion;
  total: string;
  moneda: Moneda;
}

export interface FacturaDelProyecto {
  id: string;
  serie: string | null;
  folio: number | null;
  estado: EstadoFactura;
  total: string;
  moneda: Moneda;
}

export interface ProyectoConDetalle extends ProyectoLista {
  cotizaciones: CotizacionDelProyecto[];
  facturas: FacturaDelProyecto[];
}

const CLAVE = ["proyectos"] as const;

export interface FiltrosProyectos {
  clienteId?: string;
  estado?: EstadoProyecto;
  q?: string;
}

export function useProyectos(
  filtros: FiltrosProyectos = {},
  opciones?: { habilitado?: boolean },
) {
  const parametros = new URLSearchParams();
  if (filtros.clienteId) parametros.set("clienteId", filtros.clienteId);
  if (filtros.estado) parametros.set("estado", filtros.estado);
  if (filtros.q) parametros.set("q", filtros.q);
  const cadena = parametros.toString();

  return useQuery({
    queryKey: [...CLAVE, filtros],
    queryFn: () => api<ProyectoLista[]>(`/proyectos${cadena ? `?${cadena}` : ""}`),
    // Por defecto encendida: la lista de proyectos SÍ quiere ver todo. El
    // selector ligado a un cliente es el caso que necesita apagarla —ver
    // SelectorProyecto— para no traer los proyectos de todo el mundo
    // mientras nadie ha elegido cliente todavía.
    enabled: opciones?.habilitado ?? true,
  });
}

export function useProyecto(id: string) {
  return useQuery({
    queryKey: [...CLAVE, id],
    queryFn: () => api<ProyectoConDetalle>(`/proyectos/${id}`),
    enabled: !!id,
  });
}

function useMutacionProyecto<TDatos, TVars>(
  fn: (vars: TVars) => Promise<TDatos>,
  opciones?: UseMutationOptions<TDatos, Error, TVars>,
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: fn,
    ...opciones,
    onSuccess: (...args) => {
      void cliente.invalidateQueries({ queryKey: CLAVE });
      opciones?.onSuccess?.(...args);
    },
  });
}

export const useCrearProyecto = () =>
  useMutacionProyecto((body: ProyectoCreate) =>
    api<Proyecto>("/proyectos", { method: "POST", body }),
  );

export const useActualizarProyecto = (id: string) =>
  useMutacionProyecto((body: ProyectoUpdate) =>
    api<Proyecto>(`/proyectos/${id}`, { method: "PATCH", body }),
  );

export const useEliminarProyecto = (id: string) =>
  useMutacionProyecto(() => api<void>(`/proyectos/${id}`, { method: "DELETE" }));
